// src/evaluations/EvaluationLayout.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tab,
    Tabs,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Button,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    Add as AddIcon,
    ArrowForward as ArrowForwardIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    BarChart as ChartIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import FormConfigurationLayout from './EvaluationConfigurationLayout';
import EvaluationView from './EvaluationView';
import EvaluationComparison from './EvaluationComparison';
import { useTranslation } from 'react-i18next';
// Modalidad simple / completo (PLAN_UNIFICACION_ASSESSMENT.md)
import { ASSESSMENT_MODES, getAssessmentMode } from './assessmentMode';
import AssessmentModeChip from './AssessmentModeChip';

// Filtro de modalidad de "Mis Assessments"
const MODE_FILTER_ALL = 'all';

// Componente de panel para los tabs
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`evaluation-tabpanel-${index}`}
            aria-labelledby={`evaluation-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

const EvaluationLayout = () => {
    // Estados para tabs y navegación
    const [tabValue, setTabValue] = useState(0);
    const [currentView, setCurrentView] = useState('list'); // 'list', 'edit', 'comparison'
    const [previousView, setPreviousView] = useState('list'); // Para mantener historial de navegación
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);

    // Estados para la lista de evaluaciones
    const [evaluations, setEvaluations] = useState([]);
    const [filteredEvaluations, setFilteredEvaluations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [modeFilter, setModeFilter] = useState(MODE_FILTER_ALL);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados para modal de eliminación
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [evaluationToDelete, setEvaluationToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const { authToken, showAlert, project } = useStore.getState();
    const { t } = useTranslation();

    // API Functions
    const getAllEvaluations = async () => {
        const response = await api.get("/evaluations", {
            params: { projectId: project },
            headers: { Authorization: `Bearer ${authToken}` },
        });
        return response.data;
    };

    const getEvaluationById = async (evaluationId) => {
        const response = await api.get(`/evaluations/${evaluationId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        return response.data;
    };

    const deleteEvaluationAPI = async (evaluationId) => {
        const response = await api.delete(`/evaluations/${evaluationId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        return response.data;
    };

    const loadEvaluations = async () => {
        try {
            setLoading(true);
            const response = await getAllEvaluations();
            if (response.response && response.response.status_response === 'OK') {
                setEvaluations(response.response.data || []);
                setFilteredEvaluations(response.response.data || []);
                setError(null);
            } else {
                setError(t('evaluations.loadError'));
                showAlert(t('evaluations.loadError'), "warning");
            }
        } catch (err) {
            setError(t('generalApiError'));
            showAlert(t('generalApiError'), "error");
            console.error('Error loading evaluations:', err);
        } finally {
            setLoading(false);
        }
    };

    // Cargar evaluaciones al montar el componente
    useEffect(() => {
        if (tabValue === 1 && currentView === 'list') {
            loadEvaluations();
        }
    }, [tabValue, currentView]);

    // Función para filtrar evaluaciones por búsqueda y modalidad
    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const filtered = evaluations.filter(evaluation => {
            if (modeFilter !== MODE_FILTER_ALL && getAssessmentMode(evaluation) !== modeFilter) return false;
            if (!term) return true;
            return evaluation.title?.toLowerCase().includes(term) ||
                evaluation.positionName?.toLowerCase().includes(term) ||
                evaluation.description?.toLowerCase().includes(term);
        });
        setFilteredEvaluations(filtered);
    }, [searchTerm, modeFilter, evaluations]);

    // Función para manejar cambio de tabs
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setCurrentView('list');
        setPreviousView('list');
        setSelectedEvaluation(null);
    };

    // Función para formatear fechas
    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';

        try {
            let date;

            if (dateValue && dateValue._seconds) {
                date = new Date(dateValue._seconds * 1000);
            } else if (dateValue && typeof dateValue.toDate === 'function') {
                date = dateValue.toDate();
            } else if (dateValue && dateValue.seconds) {
                date = new Date(dateValue.seconds * 1000);
            } else {
                date = new Date(dateValue);
            }

            return date.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formateando fecha:', error);
            return t('dateNotAvailable');
        }
    };

    // Función para obtener el color del chip según isActive
    const getStatusChip = (isActive) => {
        return (
            <Chip
                label={isActive ? t('active') : t('inactive')}
                color={isActive ? 'success' : 'default'}
                size="small"
            />
        );
    };

    // Función para manejar cuando se crea una evaluación exitosamente
    const handleEvaluationCreated = (evaluationData) => {
        console.log('Evaluación creada:', evaluationData);
        showAlert(t('evaluations.createSuccess'), 'success');

        setTabValue(1);
        setCurrentView('list');
        loadEvaluations();
    };

    // Función para entrar a editar evaluación
    const handleEditEvaluation = async (evaluationId) => {
        try {
            const response = await getEvaluationById(evaluationId);
            if (response.response && response.response.status_response === 'OK') {
                setSelectedEvaluation(response.response.data);
                setPreviousView(currentView); // Guardar vista anterior
                setCurrentView('edit');
            } else {
                showAlert(t('evaluations.loadErrorSingle'), 'error');
            }
        } catch (err) {
            console.error('Error loading evaluation:', err);
            showAlert(t('evaluations.loadErrorSingle'), 'error');
        }
    };

    const handleBackToList = () => {
        setCurrentView('list');
        setPreviousView('list');
        setSelectedEvaluation(null);
        loadEvaluations();
    };

    const handleBackToPrevious = () => {
        if (previousView === 'edit') {
            setCurrentView('edit');
            setPreviousView('list');
        } else {
            handleBackToList();
        }
    };

    const handleBackToEdit = () => {
        setCurrentView('edit');
        setPreviousView('list');
    };

    const handleViewComparison = async (evaluation) => {
        try {
            // Cargar datos más recientes de la evaluación
            const response = await getEvaluationById(evaluation.id);
            if (response.response && response.response.status_response === 'OK') {
                setSelectedEvaluation(response.response.data);
                setPreviousView(currentView); // Guardar vista anterior
                setCurrentView('comparison');
            } else {
                showAlert(t('evaluations.loadErrorSingle'), 'error');
            }
        } catch (err) {
            console.error('Error loading evaluation for comparison:', err);
            showAlert(t('evaluations.loadErrorSingle'), 'error');
        }
    };

    // Funciones para eliminar evaluación
    const handleDeleteEvaluation = (evaluation) => {
        setEvaluationToDelete(evaluation);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!evaluationToDelete) return;

        try {
            setDeleting(true);
            const response = await deleteEvaluationAPI(evaluationToDelete.id);

            if (response.response && response.response.status_response === 'OK') {
                showAlert(t('evaluations.deleteSuccess'), 'success');
                // Remover de la lista local
                setEvaluations(prev => prev.filter(evaluation => evaluation.id !== evaluationToDelete.id));
                setFilteredEvaluations(prev => prev.filter(evaluation => evaluation.id !== evaluationToDelete.id));
            } else {
                showAlert(t('evaluations.deleteError'), 'error');
            }
        } catch (err) {
            console.error('Error deleting evaluation:', err);
            showAlert(t('evaluations.deleteError'), 'error');
        } finally {
            setDeleting(false);
            setDeleteModalOpen(false);
            setEvaluationToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setEvaluationToDelete(null);
    };

    // Renderizar la vista de "Mis Evaluaciones"
    const renderEvaluationsList = () => (
        <Box sx={{ p: 3 }}>
            {/* Header de la tabla */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    {t('evaluations.myEvaluations')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setTabValue(0)}
                >
                    {t('evaluations.newEvaluation')}
                </Button>
            </Box>

            {/* Barra de búsqueda y filtro de modalidad */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    placeholder={t('evaluations.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flex: 1, minWidth: 260 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    label={t('evaluations.modeColumn')}
                    value={modeFilter}
                    onChange={(e) => setModeFilter(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value={MODE_FILTER_ALL}>{t('evaluations.modeFilterAll')}</MenuItem>
                    <MenuItem value={ASSESSMENT_MODES.COMPLETE}>{t('evaluations.modeChipComplete')}</MenuItem>
                    <MenuItem value={ASSESSMENT_MODES.SIMPLE}>{t('evaluations.modeChipSimple')}</MenuItem>
                </TextField>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    action={
                        <Button color="inherit" size="small" onClick={loadEvaluations}>
                            <RefreshIcon sx={{ mr: 1 }} />
                            {t('retry')}
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Loading */}
            {loading && (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Tabla de evaluaciones */}
            {!loading && !error && (
                <>
                    {filteredEvaluations.length === 0 ? (
                        <Alert severity="info">
                            {searchTerm
                                ? `${t('evaluations.noEvaluationsFound')} "${searchTerm}"`
                                : modeFilter !== MODE_FILTER_ALL
                                    ? t('evaluations.noEvaluationsForMode', {
                                        mode: modeFilter === ASSESSMENT_MODES.SIMPLE
                                            ? t('evaluations.modeChipSimple')
                                            : t('evaluations.modeChipComplete'),
                                    })
                                    : t('evaluations.noEvaluations')
                            }
                        </Alert>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('title')}</TableCell>
                                        <TableCell>{t('evaluations.modeColumn')}</TableCell>
                                        <TableCell>{t('position')}</TableCell>
                                        <TableCell>{t('drawer.applicants')}</TableCell>
                                        <TableCell>{t('drawer.capabilities')}</TableCell>
                                        <TableCell>{t('statusWord')}</TableCell>
                                        <TableCell>{t('created').charAt(0).toUpperCase() + t('created').slice(1).toLowerCase()}</TableCell>
                                        <TableCell align="center">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredEvaluations.map((evaluation) => (
                                        <TableRow key={evaluation.id}>
                                            <TableCell>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {evaluation.title}
                                                </Typography>
                                                {evaluation.description && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            maxWidth: 200,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {evaluation.description}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <AssessmentModeChip evaluation={evaluation} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {evaluation.positionName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${evaluation.applicants?.length || 0} ${t('drawer.applicants')}`.toLowerCase()}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${evaluation.capabilities?.length || 0} ${t('drawer.capabilities')}`.toLowerCase()}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(evaluation.isActive)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(evaluation.created)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewComparison(evaluation)}
                                                    title={t('evaluations.showDashboard')}
                                                    color="primary"
                                                >
                                                    <ChartIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditEvaluation(evaluation.id)}
                                                    title={t('evaluations.enterEvaluation')}
                                                    color="primary"
                                                >
                                                    <ArrowForwardIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteEvaluation(evaluation)}
                                                    title={t('delete')}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}
        </Box>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header - Solo mostrar si no estamos en vista de edición */}
            {(currentView === 'list') && (
                <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                        {t('evaluations.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('evaluations.subTitle')}
                    </Typography>
                </Box>
            )}

            {/* Tabs Navigation - Solo mostrar si no estamos en vista de edición */}
            {(currentView === 'list') && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="evaluation tabs">
                        <Tab
                            icon={<AddIcon />}
                            label={t('evaluations.newEvaluation')}
                            id="evaluation-tab-0"
                            aria-controls="evaluation-tabpanel-0"
                        />
                        <Tab
                            icon={<AssignmentIcon />}
                            label={t('evaluations.myEvaluations')}
                            id="evaluation-tab-1"
                            aria-controls="evaluation-tabpanel-1"
                        />
                    </Tabs>
                </Box>
            )}

            {/* Contenido principal */}
            {currentView === 'edit' ? (
                // Vista de edición
                <EvaluationView
                    evaluation={selectedEvaluation}
                    onBack={handleBackToList}
                    onViewComparison={() => handleViewComparison(selectedEvaluation)}
                />
            ) : currentView === 'comparison' ? (
                // Vista de dashboard/comparación
                <EvaluationComparison
                    evaluation={selectedEvaluation}
                    onBack={handleBackToPrevious}
                    onBackToList={handleBackToList}
                    onBackToEdit={handleBackToEdit}
                    previousView={previousView}
                />
            ) : (
                // Vistas de tabs normales
                <>
                    <TabPanel value={tabValue} index={0}>
                        <FormConfigurationLayout onEvaluationCreated={handleEvaluationCreated} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {renderEvaluationsList()}
                    </TabPanel>
                </>
            )}

            {/* Modal de confirmación para eliminar */}
            <Dialog
                open={deleteModalOpen}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {t('evaluations.deleteConfirm')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {`${t('evaluations.deleteConfirmText')}?`}
                    </Typography>
                    {evaluationToDelete && (
                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                {evaluationToDelete.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {evaluationToDelete.positionName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {evaluationToDelete.applicants?.length || 0} {t('drawer.applicants').toLowerCase()} • {evaluationToDelete.capabilities?.length || 0} {t('drawer.capabilities').toLowerCase()}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                        {t('noWayBack')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelDelete}
                        disabled={deleting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EvaluationLayout;